import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  type EmbeddedPaymentElementConfiguration,
  type EmbeddedPaymentElementResult,
  type IntentConfiguration,
} from '@stripe/stripe-react-native';
import { useResponsive } from '@sg/garnish';

import {
  currencyCode,
  getElementConfig,
  getStripeKeyPrefix,
  paymentMethodTypes,
  setupFutureUsage,
  useNotifyStripeCancel,
  useNotifyStripeError,
  useStripePayment,
} from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';

import { type useCreditCardOptions } from '../../../../hooks';
import {
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
} from '../../form';
import { GiftCardCheckoutFormContainer } from './components';
import { useCustomerPaymentInfoQuery } from './GraphQL/CustomerPaymentInfo.generated';
import {
  GiftCardCheckoutAmount,
  GiftCardCheckoutDeliveryDate,
  GiftCardCheckoutDeliveryMethod,
  GiftCardCheckoutDetails,
  GiftCardCheckoutLedger,
  GiftCardCheckoutPaymentElement,
  GiftCardCheckoutPaymentProvider,
  GiftCardCheckoutSubmit,
} from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutFormV2 = (props: GiftCardCheckoutFormProps) => {
  const { form, purchaseAttempt } = props;

  const paymentMethods = useMemo(() => {
    return mapPaymentMethods(props.paymentMethods);
  }, [props.paymentMethods]);

  return (
    <GiftCardCheckoutPaymentProvider form={form}>
      <GiftCardCheckoutFormReady
        {...props}
        paymentMethods={paymentMethods}
        key={getStripeKeyPrefix(purchaseAttempt)}
      />
    </GiftCardCheckoutPaymentProvider>
  );
};

const GiftCardCheckoutFormReady = (props: GiftCardCheckoutFormReadyProps) => {
  const {
    sectionTitles,
    form,
    rules,
    submitForm,
    paymentMethods,
    incrementPurchaseAttempt,
  } = props;

  const { match } = useResponsive();
  const { track } = useTelemetry();

  const { amount, recipientName } = form.watch();
  const { isSubmitting, errors } = form.formState;
  const errorKeys = Object.keys(errors).filter((key) => key !== 'paymentMethodId'); // prettier-ignore
  const canSubmit = Boolean(recipientName) && errorKeys.length === 0;

  // ─── Stripe ────────────────────────────────────────────────────────────────

  const { stripeCustomerId, stripeCustomerKey } = usePaymentInfo();

  const onStripeConfirmation = useCallback(
    (paymentMethodId: string) => {
      form.setValue('paymentMethodId', paymentMethodId);
      return submitForm();
    },
    [form, submitForm],
  );

  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('gift-card.stripe-usage', payload);
    },
    [track],
  );

  const stripeElementConfigRef = useRef<EmbeddedPaymentElementConfiguration>(
    getElementConfig({
      stripeCustomerId,
      stripeCustomerKey,
    }),
  );

  const stripeIntentConfigRef = useRef<IntentConfiguration>({
    paymentMethodTypes,
    mode: { amount, currencyCode, setupFutureUsage },
    confirmHandler: async (paymentResult, _, intentCreationCallback) => {
      trackStripeUsage({ event: 'stripe_success' });
      intentCreationCallback(SUCCESS_COMPLETE);
      onStripeConfirmation(paymentResult.id);
    },
  });

  const { embeddedPaymentElementView, selectedPaymentMethod, confirmStripe } =
    useStripePayment({
      isPaymentStatePollingEnabled: false,
      total: amount,
      paymentMethods,
      stripeIntentConfigRef,
      stripeElementConfigRef,
    });

  const notifyStripeError = useNotifyStripeError(trackStripeUsage);
  const notifyStripeCancel = useNotifyStripeCancel();

  const confirmPayment = useCallback(async () => {
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      onStripeConfirmation(paymentMethods[0].id);

      return;
    }

    if (!selectedPaymentMethod) {
      trackStripeUsage({ event: 'confirming_without_payment_option' });
    }

    const result = (await confirmStripe()) as EmbeddedPaymentElementResult & {
      paymentMethodId: string;
    };

    if (result.status === 'completed' && result.paymentMethodId) {
      trackStripeUsage({ event: 'result.completed' });
      onStripeConfirmation(result.paymentMethodId);
      return;
    }

    if (result.status === 'failed') {
      trackStripeUsage({ event: 'result.failed', error: result.error });
      notifyStripeError(result.error.message ?? result.error);
      incrementPurchaseAttempt();
      return;
    }

    if (result.status === 'canceled') {
      trackStripeUsage({ event: 'result.canceled' });
      notifyStripeCancel();
      incrementPurchaseAttempt();
    }
  }, [
    selectedPaymentMethod,
    paymentMethods,
    confirmStripe,
    trackStripeUsage,
    onStripeConfirmation,
    notifyStripeError,
    incrementPurchaseAttempt,
    notifyStripeCancel,
  ]);

  return (
    <View style={match([null, styles.containerSM])}>
      <GiftCardCheckoutFormContainer>
        <GiftCardCheckoutAmount
          sectionTitle={sectionTitles?.amount}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDetails
          sectionTitle={sectionTitles?.details}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDeliveryDate
          sectionTitle={sectionTitles?.deliveryDate}
          form={form}
          rules={rules}
        />

        <GiftCardCheckoutDeliveryMethod
          sectionTitle={sectionTitles?.deliveryMethod}
          sectionTitleEmailOnly={sectionTitles?.recipientEmail}
          form={form}
        />

        <GiftCardCheckoutPaymentElement
          embeddedPaymentElementView={embeddedPaymentElementView}
        />
      </GiftCardCheckoutFormContainer>

      <GiftCardCheckoutLedger form={form} />

      <GiftCardCheckoutSubmit
        isDisabled={!canSubmit}
        isSubmitting={isSubmitting}
        onPress={confirmPayment}
      />
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const usePaymentInfo = () => {
  const [response] = useCustomerPaymentInfoQuery({
    requestPolicy: 'cache-only',
  });

  const data = response.data?.customerPaymentInfo;
  const stripeCustomerId = data?.id ?? '';
  const stripeCustomerKey = data?.paymentEphemeralKey ?? '';
  const stripeSession = data?.paymentSessionId ?? '';

  return {
    stripeCustomerId,
    stripeCustomerKey,
    stripeSession,
  };
};

const mapPaymentMethods = (
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'],
) => {
  return paymentMethods.map((paymentMethod) => ({
    id: paymentMethod.value,
    description: paymentMethod.label,
    nickname: paymentMethod.label,
    cardType: paymentMethod.cardType as any,
    isDefault: paymentMethod.isDefault,
  }));
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutFormProps = {
  purchaseAttempt: number;
  incrementPurchaseAttempt: () => void;
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'];
  submitForm: () => Promise<void>;
  sectionTitles?: {
    amount: string;
    details: string;
    deliveryDate: string;
    deliveryMethod: string;
    recipientEmail: string;
    paymentMethod: string;
  };
};

type GiftCardCheckoutFormReadyProps = Omit<
  GiftCardCheckoutFormProps,
  'paymentMethods'
> & {
  paymentMethods: ReturnType<typeof mapPaymentMethods>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NO_INTENT_COMPLETE = 'COMPLETE_WITHOUT_CONFIRMING_INTENT';
const SUCCESS_COMPLETE = { clientSecret: NO_INTENT_COMPLETE };
